'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { buttonVariants } from './variants';
import { sendGTMEvent } from '@next/third-parties/google';

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export type ButtonBaseProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'ref'
> &
  ButtonVariantProps & {
    asChild?: boolean;
    loading?: boolean;
    'data-ta'?: string;
  };

/**
 * `Button` is a React component that renders a GDS themed button element with various styles and behaviors.
 * It supports different variants, sizes, loading states, and can render custom child components.
 *
 * @example
 * ```tsx
 * import { Button } from 'components/ui/button';
 * import LinkPrimitive from 'next/link';
 *
 * // Generic button examples
 * <Button>Click Me</Button>
 * <Button variant="secondary">Click Me</Button>
 * // Tomato Call to action
 * <Button variant="cta">Click Me</Button>
 * // Loading spinner will be displayed inside the button
 * <Button loading>Click Me</Button>
 * // Text styled button
 * <Button variant="text">Click Me</Button>
 * // Link styled button
 * // defaults to default link styles, i.e. `inlineLink` variant, `hugoBlueShade` linkColor and `lg` size
 * <Button variant="link">Link Button</Button>
 * </Button>
 * // Full width button
 * <Button fullWidth>Click Me</Button>
 * // Custom data-ta for easier testing identification
 * <Button data-ta="custom-button">Click Me</Button>
 * // Button as a child element
 * <Button asChild>
 *  <div>Div with Button Styles</div>
 * </Button>
 * ```
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonBaseProps>(
  (
    {
      type = 'button',
      className,
      variant,
      disabled,
      asChild = false,
      loading,
      children,
      ...props
    },
    ref
  ) => {
    const sharedStyles = cn(
      buttonVariants({ variant }),
      loading && 'cursor-not-allowed',
      className
    );

    const loader = loading && (
      <Loader2 className={cn('h-4 w-4 animate-spin', children && 'mr-2')} />
    );

    if (asChild) {
      const onlyChild = React.Children.only(
        children as React.ReactElement
      );
      return (
        <Slot ref={ref} {...props}>
          {React.cloneElement(onlyChild, {
            className: sharedStyles,
            disabled: disabled || loading,
            ['data-ta']: props['data-ta'],
            children: (
              <>
                {loader}
                {onlyChild.props.children}
              </>
            ),
          })}
        </Slot>
      );
    }

    function handleButtonClick(props: ButtonBaseProps): void {
      sendGTMEvent({
        event: 'click_event',
        event_name: props['data-ta'],
      });
    }

    return (
      <button
        type={type}
        className={sharedStyles}
        disabled={disabled || loading}
        data-ta={props['data-ta']}
        ref={ref}
        {...props}
        onClick={(e) => {
          handleButtonClick({ ...props });
          if (props.onClick) {
            props.onClick(e);
          }
        }}
      >
        <>
          {loader}
          {children}
        </>
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
